import dayjs from 'dayjs'

export const DATE_FORMAT = 'YYYY-MM-DD'
export const ISO_DATE_FORMAT = 'YYYY-MM-DD'

export const ACCEPTED_EXCEL_IMPORT_DATE_FORMATS = ['DD.MM.YYYY', 'DD/MM/YYYY', 'DD-MM-YYYY', 'MM/DD/YY', 'M/D/YY']

export const EXCEL_LABELS = {
  ORDER_NUMBER: 'PO Number',
  ORIGINAL_MILESTONE_DATE: 'Original Milestone Date',
  FORECASTED_MILESTONE_DATE: 'Forecasted Milestone Date',
  EXPECTED_INVOICE_DATE: 'Expected Invoice Date',
  EXPECTED_PAYMENT_DATE: 'Expected Payment Date',
  MILESTONE_NAME: 'Milestone Name',
  SUB_MILESTONE_NAME: 'SubMilestone Name',
  AMOUNT: 'Amount',
  SUBSTANTIATION: 'Substantiation'
}

export const VARIATIONS_HEADERS = {
  variation: [
    {
      key: 'no',
      value: 'no',
      sort: (a, b) => {
        const parse = (string) => parseInt(string.replace(/\D/g, '')) || 0
        return parse(a) - parse(b)
      }
    },
    { key: 'title', value: 'name' },
    { key: 'lastUpdate', value: 'lastUpdate' },
    { key: 'referenceNumber', value: 'referenceNumber' },
    { key: 'value', value: 'value' },
    { key: 'eot', value: 'eot' },
    { key: 'notes', value: 'description' },
    { key: 'status', value: 'status' },
    { value: 'actions', sortable: false },
    { value: 'data-table-expand' }
  ],
  claim: [
    { key: 'no', value: 'no' },
    { key: 'title', value: 'name' },
    { key: 'lastUpdate', value: 'lastUpdate' },
    { key: 'referenceNumber', value: 'referenceNumber' },
    { key: 'value', value: 'value' },
    { key: 'impact', value: 'eot' },
    { key: 'notes', value: 'description' },
    { key: 'status', value: 'status' },
    { text: '', value: 'actions', sortable: false },
    { text: '', value: 'data-table-expand' }
  ],
  amendment: [
    { key: 'no', value: 'no' },
    { key: 'title', value: 'name' },
    { key: 'lastUpdate', value: 'lastUpdate' },
    { key: 'referenceNumber', value: 'referenceNumber' },
    { key: 'value', value: 'value' },
    { key: 'status', value: 'status' },
    { value: 'actions', sortable: false },
    { value: 'data-table-expand' }
  ],
  early_warning: [
    { key: 'no', value: 'no' },
    { key: 'title', value: 'name' },
    { key: 'lastUpdate', value: 'lastUpdate' },
    { key: 'referenceNumber', value: 'referenceNumber' },
    { key: 'value', value: 'value' },
    { key: 'status', value: 'status' },
    { value: 'actions', sortable: false },
    { value: 'data-table-expand' }
  ]
}

export const SITES = {
  employer: 'employer',
  contractor: 'contractor'
}

export const getSumOfPrices = (array, fieldName, currencies = []) => {
  return array.reduce((acc, curr) => {
    const currency = ((curr.currency || curr.part)
      ? currencies.find(i => i.key === (curr.currency ? (curr.currency.key || curr.currency) : curr.part.currency))
      : currencies.find(i => i.value === 1)) || { key: 'EUR', value: 1 }
    const priceValue = isNaN(curr[fieldName]) ? curr[fieldName].sum : curr[fieldName]
    acc.sum += (currency.value * priceValue)
    acc[currency.key] = (acc[currency.key] || 0) + priceValue
    return acc
  }, { sum: 0 })
}

export const calculateBaseCurrencyCentsToChosen = (sum, baseCurrency, chosenCurrency) => {
  return baseCurrency.key === chosenCurrency.key ? sum : sum / chosenCurrency.value
}

export const checkMilestonesNamesDuplicates = (milestone, milestonesList, maxOccurrences = 1, includeSubMilestones = true) => {
  const namesList = milestonesList.reduce((acc, curr) => [
    ...acc,
    curr.name,
    ...(includeSubMilestones ? curr.subMilestones.map(i => i.name) : [])
  ], [])
  return namesList.filter(i => i === milestone.name).length >= maxOccurrences
}

export const mapEnvAppColor = () => {
  const nodeEnv = process.env.VUE_APP_ENV
  const envMap = {
    development: 'light-green',
    production: 'white',
    acceptance: 'light-blue',
    testing: 'amber',
    local: 'lime'
  }

  const mappedColor = envMap[nodeEnv] || 'white'
  return mappedColor
}

export const mapEnvAppName = () => {
  const nodeEnv = process.env.VUE_APP_ENV
  const envMap = {
    development: 'CCA: Development',
    acceptance: 'CCA: Acceptance',
    testing: 'CCA: TEST',
    local: 'CCA: LOCAL'
  }
  const mappedName = envMap[nodeEnv] || 'Commercial Contract App'
  return mappedName
}

export const toFixedWithoutRounding = (number, fixed) => {
  const regexp = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?')
  return number?.toString().match(regexp)[0]
}

export const formatValue = (value) => {
  if (typeof value === 'number') {
    const formattedValue = (value / 100).toFixed(2)
    return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }
  return value
}

export const formatDate = (date) => {
  return date ? (dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : '') : ''
}

export const lightenDarkenColor = (col, amt) => {
  let usePound = false

  if (col[0] === '#') {
    col = col.slice(1)
    usePound = true
  }

  const num = parseInt(col, 16)

  let r = (num >> 16) + amt

  if (r > 255) r = 255
  else if (r < 0) r = 0

  let b = ((num >> 8) & 0x00FF) + amt

  if (b > 255) b = 255
  else if (b < 0) b = 0

  let g = (num & 0x0000FF) + amt

  if (g > 255) g = 255
  else if (g < 0) g = 0

  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16)
}

export const capitalizeFirstLetter = (string) => {
  if (!string) return ''
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export * from './defaults'
export * from './insightsCharts'
export * from './paymentCertification'
export * from './pdfHelper'
export * from './contractPayments'
